import { ArrowLeft } from '@phosphor-icons/react';
import { Button, cn, Inline, Stack } from '@reelplicate/ui';
import type { ComponentProps, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';

/**
 * Figma "Form column" of each onboarding step: a 600px bg/surface card,
 * radius/2xl, border/subtle, Shadow/SM, 40px padding (24 on phones). The
 * space between header, body and actions is 24 on step 1-2, 28 after.
 */
export function OnboardingCard({
  loose = false,
  className,
  ...props
}: ComponentProps<'form'> & { loose?: boolean }) {
  return (
    <form
      noValidate
      className={cn(
        'bg-bg-surface border-border-subtle shadow-sm flex w-full max-w-[600px] flex-col rounded-2xl border p-6 tablet:p-10',
        loose ? 'gap-7' : 'gap-6',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The step header: Overline eyebrow in text/brand (or a visual such as a
 * status icon), Heading/H1 title and a Body/Large subtitle, 10px apart
 * (12 on the finishing screens).
 */
export function OnboardingHeader({
  eyebrow,
  visual,
  title,
  subtitle,
  align = 'left',
  loose = false,
}: {
  eyebrow?: string;
  visual?: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
  align?: 'left' | 'center';
  loose?: boolean;
}) {
  return (
    <Stack
      as="header"
      align={align}
      className={cn(loose ? 'gap-3' : 'gap-2.5', align === 'center' && 'text-center')}
    >
      {visual}
      {eyebrow && <p className="text-overline text-text-brand uppercase">{eyebrow}</p>}
      <h1 className="text-heading-h1">{title}</h1>
      <p className="text-body-lg text-text-secondary">{subtitle}</p>
    </Stack>
  );
}

/**
 * Figma "Actions": an optional ghost Back on the left, then the step's
 * buttons on the right (Large, 12px apart).
 */
export function OnboardingActions({
  back,
  children,
}: {
  back?: string;
  children: ReactNode;
}) {
  return (
    <Inline gap={3} wrap>
      {back && (
        <Button asChild variant="ghost" size="lg" leadingIcon={ArrowLeft}>
          <RouterLink to={back}>Back</RouterLink>
        </Button>
      )}
      <Inline gap={3} wrap className="ml-auto">
        {children}
      </Inline>
    </Inline>
  );
}
